import { MenuBar } from "../../controls/MenuBar.js";
import { menuSeparator, type MenuItemSpec } from "../../controls/MenuItem.js";
import { StatusBar, type StatusPanelSpec } from "../../controls/StatusBar.js";
import { Toolbar, type ToolbarItemSpec } from "../../controls/Toolbar.js";
import { registerElement, type Attrs, type BuildContext } from "../registry.js";
import { isAbsolute, toMnemonic, type Length } from "../values.js";
import type { XmlElement } from "../xml.js";

const opts = <T>(attrs: Attrs): T => attrs as T;

/**
 * The bars, whose children are specs rather than controls.
 *
 * A menu row is a `MenuItemSpec`, not a control, and writing one as `<Item text="_Save"/>` is
 * both shorter than the object literal and closer to what a menu is. The tag names are
 * deliberately the same across all three — `<Item>`, `<Separator>` — because the registry
 * resolves them through the parent, so what they mean follows the bar they are in.
 */
export function registerBars(): void {
  registerElement<MenuBar>({
    tag: "MenuBar",
    create: (attrs) => new MenuBar(opts(attrs)),
    childSpecs: {
      Menu(element, ctx, bar) {
        bar.items = [...bar.items, readMenu(element, ctx)];
      },
    },
  });

  registerElement<Toolbar>({
    tag: "Toolbar",
    attributes: {
      display: ["icon", "text", "both"],
      iconSize: "number",
      overflow: "boolean",
    },
    create: (attrs) => new Toolbar(opts(attrs)),
    childSpecs: {
      Item(element, ctx, bar) {
        const attrs = ctx.attributesOf(element, {
          text: "string",
          tooltip: "string",
          enabled: "boolean",
          checked: "boolean",
          toggle: "boolean",
          split: "boolean",
          name: "string",
        });
        const item: ToolbarItemSpec = {
          text: toMnemonic((attrs["text"] as string) ?? element.text),
          ...pick(attrs, "tooltip", "enabled", "checked", "toggle", "split"),
        };
        const menu = element.children.filter((c) => c.name === "Menu" || c.name === "Item");
        if (menu.length > 0) {
          item.menu = element.children.map((c) => readMenuChild(c, ctx)).filter(isSpec);
        }
        bindClick(element, ctx, item);
        bar.items = [...bar.items, item];
        nameIt(attrs, ctx, item);
      },
      Separator(_element, _ctx, bar) {
        bar.items = [...bar.items, { separator: true }];
      },
    },
  });

  registerElement<StatusBar>({
    tag: "StatusBar",
    attributes: { showGrip: "boolean", font: "font" },
    create: (attrs) => new StatusBar(opts(attrs)),
    childSpecs: {
      Item(element, ctx, bar) {
        const attrs = ctx.attributesOf(element, {
          text: "string",
          width: "length",
          align: ["left", "center", "right"],
          style: ["none", "sunken", "raised"],
          tooltip: "string",
          name: "string",
        });
        const width = attrs["width"] as Length | undefined;
        const panel: StatusPanelSpec = {
          text: (attrs["text"] as string) ?? element.text,
          // `width="*"` is the same word the rest of the dialect uses for "take what is left",
          // and `"spring"` is what a status bar has always called it. Anything absolute goes
          // through `toPixels`, so `8em` is eight of the theme's font sizes and not the number 8.
          ...(width && width.kind === "star" ? { width: "spring" as const } : {}),
          ...pick(attrs, "align", "style", "tooltip"),
        };
        // Bound rather than computed, so `width="8em"` follows a theme swap the way the bar's own
        // height does. The bar reads the spec on every paint, so writing to it is enough.
        if (width && isAbsolute(width)) {
          ctx.bindEm(width, (px) => {
            panel.width = px;
            bar.invalidate();
          });
        }
        bindClick(element, ctx, panel);
        bar.panels = [...bar.panels, panel];
        nameIt(attrs, ctx, panel);
      },
    },
  });
}

/** A `<Menu>` and everything under it, as one spec with a submenu. */
function readMenu(element: XmlElement, ctx: BuildContext): MenuItemSpec {
  const attrs = ctx.attributesOf(element, {
    text: "string",
    enabled: "boolean",
    name: "string",
  });
  const spec: MenuItemSpec = {
    text: toMnemonic((attrs["text"] as string) ?? ""),
    ...pick(attrs, "enabled"),
    items: element.children.map((c) => readMenuChild(c, ctx)).filter(isSpec),
  };
  nameIt(attrs, ctx, spec);
  return spec;
}

function readMenuChild(element: XmlElement, ctx: BuildContext): MenuItemSpec | null {
  if (element.name === "Separator") return menuSeparator;
  if (element.name === "Menu") return readMenu(element, ctx);
  if (element.name !== "Item") return null;

  const attrs = ctx.attributesOf(element, {
    text: "string",
    shortcut: "string",
    checked: "boolean",
    radio: "boolean",
    enabled: "boolean",
    name: "string",
  });
  const spec: MenuItemSpec = {
    text: toMnemonic((attrs["text"] as string) ?? element.text),
    ...pick(attrs, "shortcut", "checked", "radio", "enabled"),
  };
  const children = element.children.map((c) => readMenuChild(c, ctx)).filter(isSpec);
  if (children.length > 0) spec.items = children;
  bindClick(element, ctx, spec);
  nameIt(attrs, ctx, spec);
  return spec;
}

function isSpec(spec: MenuItemSpec | null): spec is MenuItemSpec {
  return spec !== null;
}

/**
 * A spec's `onClick` is a handler name, the same as a control's.
 *
 * It is called with the spec rather than a control, which is what these bars pass, and the view
 * comes along so a handler can reach the rest of the document without a closure.
 */
function bindClick(
  element: XmlElement,
  ctx: BuildContext,
  spec: { onClick?: (item: never) => void },
): void {
  const attribute = element.attributes.find((a) => a.name === "onClick");
  if (!attribute) return;
  const handler = ctx.handler(attribute.value, element);
  spec.onClick = (item: never) => handler(item, spec as never, ctx.view);
}

function nameIt(attrs: Attrs, ctx: BuildContext, spec: unknown): void {
  const name = attrs["name"];
  if (typeof name === "string" && name) ctx.nameSpec(name, spec);
}

/** Copy only the keys that were actually written, so a spec has no undefined fields in it. */
function pick<K extends string>(attrs: Attrs, ...keys: K[]): Partial<Record<K, never>> {
  const out: Record<string, unknown> = {};
  for (const key of keys) if (attrs[key] !== undefined) out[key] = attrs[key];
  return out as Partial<Record<K, never>>;
}
